import { useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  ColumnLayout,
  Container,
  FormField,
  Header,
  Input,
  Link,
  Modal,
  SpaceBetween,
  StatusIndicator,
  Table,
  Textarea,
  type TableProps,
} from '@cloudscape-design/components';
import {
  fetchAcpPanels,
  fetchAcpPanel,
  registerAcpPanel,
  deployAcpPanel,
  healthCheckAcpPanel,
  type AcpPanel,
  type AcpPanelDetail,
  type AcpHealthCheckResult,
} from '../api/acp-panels';

const STATUS_MAP: Record<string, { type: 'success' | 'in-progress' | 'stopped'; label: string }> = {
  active: { type: 'success', label: 'Active' },
  provisioning: { type: 'in-progress', label: 'Provisioning' },
  inactive: { type: 'stopped', label: 'Inactive' },
};

function formatDate(iso: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function AcpPanels() {
  const [panels, setPanels] = useState<AcpPanel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Register modal
  const [showRegister, setShowRegister] = useState(false);
  const [regSlug, setRegSlug] = useState('');
  const [regCustomerId, setRegCustomerId] = useState('');
  const [regDomain, setRegDomain] = useState('');
  const [regDescription, setRegDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Detail modal
  const [detailPanel, setDetailPanel] = useState<AcpPanelDetail | null>(null);
  const [showDetail, setShowDetail] = useState(false);

  // Deploy modal
  const [showDeploy, setShowDeploy] = useState(false);
  const [deploySlug, setDeploySlug] = useState('');
  const [deploySource, setDeploySource] = useState('');
  const [deployVersion, setDeployVersion] = useState('');

  // Health check
  const [healthResults, setHealthResults] = useState<Record<string, AcpHealthCheckResult>>({});
  const [healthLoading, setHealthLoading] = useState<string | null>(null);

  useEffect(() => { loadPanels(); }, []);

  async function loadPanels() {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAcpPanels();
      setPanels(data.panels);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  async function handleRegister() {
    if (!regSlug) return;
    setSubmitting(true);
    try {
      await registerAcpPanel({
        slug: regSlug,
        productId: 'acp',
        customerId: regCustomerId || regSlug,
        domain: regDomain || undefined,
        description: regDescription || undefined,
      });
      setShowRegister(false);
      setRegSlug(''); setRegCustomerId(''); setRegDomain(''); setRegDescription('');
      loadPanels();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to register');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleViewDetail(slug: string) {
    try {
      const detail = await fetchAcpPanel(slug);
      setDetailPanel(detail);
      setShowDetail(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load details');
    }
  }

  async function handleDeploy() {
    if (!deploySlug) return;
    setSubmitting(true);
    try {
      await deployAcpPanel(deploySlug, {
        source: deploySource || undefined,
        version: deployVersion || undefined,
      });
      setShowDeploy(false);
      setDeploySlug(''); setDeploySource(''); setDeployVersion('');
      loadPanels();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Deploy failed');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleHealthCheck(slug: string) {
    setHealthLoading(slug);
    try {
      const result = await healthCheckAcpPanel(slug);
      setHealthResults((prev) => ({ ...prev, [slug]: result }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Health check failed');
    } finally {
      setHealthLoading(null);
    }
  }

  const columns: TableProps.ColumnDefinition<AcpPanel>[] = [
    {
      id: 'slug',
      header: 'Slug',
      width: 140,
      cell: (item) => (
        <Button variant="inline-link" onClick={() => handleViewDetail(item.slug)}>
          {item.slug}
        </Button>
      ),
    },
    {
      id: 'domain',
      header: 'Domain',
      width: 240,
      cell: (item) => (
        <Link external href={`https://${item.domain}`}>{item.domain}</Link>
      ),
    },
    {
      id: 'customer',
      header: 'Customer',
      width: 140,
      cell: (item) => item.customerId || '—',
    },
    {
      id: 'status',
      header: 'Status',
      width: 120,
      cell: (item) => {
        const s = STATUS_MAP[item.status] || STATUS_MAP.inactive;
        return <StatusIndicator type={s.type}>{s.label}</StatusIndicator>;
      },
    },
    {
      id: 'version',
      header: 'Version',
      width: 110,
      cell: (item) => item.currentVersion || '—',
    },
    {
      id: 'deploys',
      header: 'Deploys',
      width: 80,
      cell: (item) => item.deployCount || 0,
    },
    {
      id: 'lastDeploy',
      header: 'Last Deploy',
      width: 160,
      cell: (item) => formatDate(item.lastDeployAt),
    },
    {
      id: 'health',
      header: 'Health',
      width: 130,
      cell: (item) => {
        const h = healthResults[item.slug];
        if (healthLoading === item.slug) return <StatusIndicator type="loading">Checking...</StatusIndicator>;
        if (!h) return <Button variant="inline-link" onClick={() => handleHealthCheck(item.slug)}>Check</Button>;
        return h.code >= 200 && h.code < 400
          ? <StatusIndicator type="success">{h.code} ({h.latencyMs}ms)</StatusIndicator>
          : <StatusIndicator type="error">{h.error || h.code}</StatusIndicator>;
      },
    },
    {
      id: 'actions',
      header: '',
      width: 100,
      cell: (item) => (
        <Button variant="normal" onClick={() => { setDeploySlug(item.slug); setShowDeploy(true); }}>
          Deploy
        </Button>
      ),
    },
  ];

  return (
    <SpaceBetween size="l">
      <Header
        variant="h1"
        description="Agentic Control Panels — per-customer panels served at {slug}.panels.gdna.io."
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button iconName="refresh" onClick={loadPanels}>Refresh</Button>
            <Button variant="primary" onClick={() => setShowRegister(true)}>Register Panel</Button>
          </SpaceBetween>
        }
      >
        ACP Panels
      </Header>

      {error && <Alert type="error" dismissible onDismiss={() => setError(null)}>{error}</Alert>}

      <Table
        columnDefinitions={columns}
        items={panels}
        loading={loading}
        loadingText="Loading ACP panels..."
        trackBy="slug"
        variant="full-page"
        stickyHeader
        stripedRows
        empty={
          <Box textAlign="center" padding="l">
            <Box variant="p" color="text-status-inactive">No ACP panels registered yet.</Box>
            <Button variant="primary" onClick={() => setShowRegister(true)}>Register Panel</Button>
          </Box>
        }
        header={<Header counter={`(${panels.length})`}>All Panels</Header>}
      />

      {/* Register Modal */}
      <Modal
        visible={showRegister}
        onDismiss={() => setShowRegister(false)}
        header="Register ACP Panel"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setShowRegister(false)}>Cancel</Button>
              <Button variant="primary" onClick={handleRegister} loading={submitting} disabled={!regSlug}>Register</Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <FormField label="Slug" description="URL-safe identifier — becomes {slug}.panels.gdna.io" constraintText="Lowercase, alphanumeric, hyphens only">
            <Input value={regSlug} onChange={({ detail }) => setRegSlug(detail.value)} placeholder="glitchrig" />
          </FormField>
          <FormField label="Customer ID" description="Defaults to the slug if left blank">
            <Input value={regCustomerId} onChange={({ detail }) => setRegCustomerId(detail.value)} placeholder="glitchrig" />
          </FormField>
          <FormField label="Domain" description="Leave blank to auto-generate ({slug}.panels.gdna.io)">
            <Input value={regDomain} onChange={({ detail }) => setRegDomain(detail.value)} placeholder="glitchrig.panels.gdna.io" />
          </FormField>
          <FormField label="Description">
            <Textarea value={regDescription} onChange={({ detail }) => setRegDescription(detail.value)} rows={2} />
          </FormField>
        </SpaceBetween>
      </Modal>

      {/* Deploy Modal */}
      <Modal
        visible={showDeploy}
        onDismiss={() => setShowDeploy(false)}
        header={`Deploy: ${deploySlug}`}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setShowDeploy(false)}>Cancel</Button>
              <Button variant="primary" onClick={handleDeploy} loading={submitting}>Deploy</Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <FormField label="Source (S3 Key)" description="Path to the panel bundle zip in the templates bucket">
            <Input value={deploySource} onChange={({ detail }) => setDeploySource(detail.value)} placeholder="products/glitchrig/0.1.0/panel-dist.zip" />
          </FormField>
          <FormField label="Version Label">
            <Input value={deployVersion} onChange={({ detail }) => setDeployVersion(detail.value)} placeholder="0.1.0" />
          </FormField>
        </SpaceBetween>
      </Modal>

      {/* Detail Modal */}
      <Modal
        visible={showDetail}
        onDismiss={() => setShowDetail(false)}
        header={detailPanel ? `${detailPanel.slug} — ${detailPanel.domain}` : 'Details'}
        size="large"
      >
        {detailPanel && (
          <SpaceBetween size="m">
            <ColumnLayout columns={3} variant="text-grid">
              <SpaceBetween size="xxs">
                <Box variant="awsui-key-label">Domain</Box>
                <Link external href={`https://${detailPanel.domain}`}>{detailPanel.domain}</Link>
              </SpaceBetween>
              <SpaceBetween size="xxs">
                <Box variant="awsui-key-label">Customer</Box>
                <Box>{detailPanel.customerId || '—'}</Box>
              </SpaceBetween>
              <SpaceBetween size="xxs">
                <Box variant="awsui-key-label">S3 Bucket</Box>
                <Box><code>{detailPanel.s3Bucket}</code></Box>
              </SpaceBetween>
              <SpaceBetween size="xxs">
                <Box variant="awsui-key-label">CloudFront</Box>
                <Box><code>{detailPanel.cfDistributionId || '—'}</code></Box>
              </SpaceBetween>
              <SpaceBetween size="xxs">
                <Box variant="awsui-key-label">Current Version</Box>
                <Box>{detailPanel.currentVersion || '—'}</Box>
              </SpaceBetween>
              <SpaceBetween size="xxs">
                <Box variant="awsui-key-label">Total Deploys</Box>
                <Box>{detailPanel.deployCount}</Box>
              </SpaceBetween>
            </ColumnLayout>

            {detailPanel.deployHistory.length > 0 && (
              <Table
                columnDefinitions={[
                  { id: 'version', header: 'Version', cell: (d) => d.version, width: 150 },
                  { id: 'files', header: 'Files', cell: (d) => d.files, width: 80 },
                  { id: 'source', header: 'Source', cell: (d) => d.source, width: 200 },
                  { id: 'deployedBy', header: 'By', cell: (d) => d.deployedBy, width: 120 },
                  { id: 'deployedAt', header: 'Deployed', cell: (d) => formatDate(d.deployedAt), width: 160 },
                ]}
                items={detailPanel.deployHistory}
                trackBy="deployedAt"
                header={<Header counter={`(${detailPanel.deployHistory.length})`}>Deploy History</Header>}
              />
            )}
          </SpaceBetween>
        )}
      </Modal>
    </SpaceBetween>
  );
}
